// src/shared/utils/chartFilename.ts
//
// Centralized, filesystem-safe filename builder for chart image exports.
// Keeping this in one place guarantees every downloadable chart across the
// app produces a consistent, traceable name of the shape:
//
//   <slug>_<part>_<part>_<local-timestamp>
//
// e.g. financial-gravity_3Y_2026-09-17T08-32-05
//
// The timestamp is derived from the user's LOCAL time (not UTC) so the
// filename matches the wall-clock moment they clicked "Download", and every
// character that is illegal on Windows/macOS filesystems (: / \ etc.) is
// stripped or replaced.

/** Characters that are unsafe in filenames on common OSes. */
const UNSAFE_CHARS = /[<>:"/\\|?*\u0000-\u001F]/g;

/**
 * Produce a local-time timestamp safe for filenames, e.g. "2026-09-17T08-32-05".
 * Uses local time (via offset correction) rather than UTC.
 */
export function chartTimestamp(date: Date = new Date()): string {
    const tzOffsetMs = date.getTimezoneOffset() * 60_000;
    const local = new Date(date.getTime() - tzOffsetMs);
    // "2026-09-17T08:32:05.123Z" -> "2026-09-17T08-32-05"
    return local.toISOString().slice(0, 19).replace(/:/g, '-');
}

/**
 * Turn an arbitrary label into a filename-safe slug fragment.
 * Collapses whitespace/illegal chars to single hyphens.
 */
export function slugifyPart(part: string): string {
    return part
        .trim()
        .replace(UNSAFE_CHARS, '-')
        .replace(/[\s._]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * Build a consistent chart export base filename (WITHOUT extension).
 *
 * @param slug  Stable chart identifier, e.g. "financial-gravity".
 * @param parts Optional context fragments (time window, tenors, resolved
 *              date). Empty/nullish fragments are ignored.
 *
 * @example buildChartFilename('yield-series', ['3Y', 'year2-year10'])
 *          => "yield-series_3Y_year2-year10_2026-09-17T08-32-05"
 */
export function buildChartFilename(
    slug: string,
    parts: Array<string | number | null | undefined> = [],
): string {
    const cleanParts = parts
        .filter((p): p is string | number => p !== null && p !== undefined && `${p}`.length > 0)
        .map((p) => slugifyPart(`${p}`))
        .filter((p) => p.length > 0);

    return [slugifyPart(slug), ...cleanParts, chartTimestamp()].join('_');
}
